import { getDistance as haversineDist } from 'ol/sphere';
import { isPilotPointName, legForEndPoint, pruneRouteGeometry, toDegrees, toRadians } from './helpers';
import { VoyageRoutePointFeature, VoyageRouteLineFeature, VoyageRoute, WaypointUserInput } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { ImportResult } from './types/converters.types';
import { DOMParser as XMLDOMParser } from '@xmldom/xmldom';

const NMToMeters = 1852; // Nautical miles -> meters
const KtsToMps = 1852 / (60 * 60); // Knots -> meters per second

function isLikelyNacosRouteModelXml(str: string): boolean {
    // Quick, non-DOM pre-check to avoid parsing arbitrary text as XML.
    // We only proceed if the input appears to contain the expected RouteModel
    // root element and NACOS namespace.
    if (!str) {
        return false;
    }
    const hasRouteModelTag = str.indexOf('<RouteModel') !== -1;
    const hasExpectedNamespace = str.indexOf('xmlns="http://www.sam-electronics.de/2010/reducedRouteModel.xsd"') !== -1;
    return hasRouteModelTag && hasExpectedNamespace;
}

export class NacosConverter {
    static check(str: string) {
        try {
            // Avoid parsing if the string does not even look like the expected NACOS XML.
            if (!isLikelyNacosRouteModelXml(str)) {
                return;
            }

            const doc = NacosConverter._parseXmlDocument(str);

            const root = doc.documentElement;

            if (root && root.nodeName === 'RouteModel' &&
                root.getAttribute('xmlns') === 'http://www.sam-electronics.de/2010/reducedRouteModel.xsd') {
                return doc;
            }
        } catch(e) {
            // don't care
        }
    }

    static import(str: string, options = {}): ImportResult|undefined {
        const opt = {
            assumeGCAbove: 300,
            ...options
        }

        try {
            const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>  = [];

            const doc = NacosConverter.check(str);
            if (!doc) return;
            const waypoints = doc.getElementsByTagName('Waypoints');
            let prevCoords: [number,number]|null = null;

            Array.from(waypoints).forEach(waypoint => {
                const longitudeText = NacosConverter._querySelector(waypoint, 'Longitude')?.textContent;
                const latitudeText = NacosConverter._querySelector(waypoint, 'Latitude')?.textContent;
                const coords: [number, number] = [
                    toDegrees(Number.parseFloat(longitudeText!)),
                    toDegrees(Number.parseFloat(latitudeText!))
                ];

                const name = NacosConverter._querySelector(waypoint, 'Name');
                const nameText = name ? name.textContent! : '';
                const isPilotPoint = isPilotPointName(nameText);
                // NACOS encodes sail mode on the destination waypoint.
                const sailMode = NacosConverter._querySelector(waypoint, 'SailMode');
                const radiusText = NacosConverter._querySelector(waypoint, 'Radius')?.textContent;
                const maxSpeedText = NacosConverter._querySelector(waypoint, 'MaximalSpeed')?.textContent;
                const rateOfTurnText = NacosConverter._querySelector(waypoint, 'RateOfTurn')?.textContent;
                const radiusMeters = radiusText ? Number.parseFloat(radiusText) : undefined;
                const maxSpeedKnots = maxSpeedText ? Number.parseFloat(maxSpeedText) : undefined;
                const rateOfTurn = rateOfTurnText ? Number.parseFloat(rateOfTurnText) : undefined;
                const maxSpeedMps = Number.isFinite(maxSpeedKnots) && maxSpeedKnots! > 0
                    ? maxSpeedKnots! * KtsToMps
                    : undefined;

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties: {
                        type: 'Hard',
                        name: nameText,
                        pilotPoint: isPilotPoint ? true : undefined
                    }
                };

                const userInput: WaypointUserInput = {};
                let turnRadiusNm: number|undefined;
                if (Number.isFinite(radiusMeters) && radiusMeters! > 0) {
                    turnRadiusNm = radiusMeters! / NMToMeters;
                    userInput.turn_radius = turnRadiusNm;
                }

                if (Number.isFinite(rateOfTurn)) {
                    userInput.rate_of_turn = rateOfTurn;
                } else if (turnRadiusNm && Number.isFinite(maxSpeedKnots) && maxSpeedKnots! > 0) {
                    userInput.rate_of_turn = (maxSpeedKnots! / turnRadiusNm) * (180 / Math.PI) / 60;
                }

                if (maxSpeedMps !== undefined) {
                    userInput.speed_maximum = maxSpeedMps;
                }

                if (Object.keys(userInput).length) {
                    wPoint.properties.userInput = userInput;
                    if (userInput.turn_radius !== undefined) {
                        wPoint.properties.turn_radius = userInput.turn_radius;
                    }
                    if (userInput.rate_of_turn !== undefined) {
                        wPoint.properties.rate_of_turn = userInput.rate_of_turn;
                    }
                    if (userInput.speed_maximum !== undefined) {
                        wPoint.properties.speed_maximum = userInput.speed_maximum;
                    }
                }

                if (prevCoords) {
                    const dist = haversineDist(prevCoords, coords) / NMToMeters; // Accept minimal error on dist, assuming GC for RL

                    let isGC = true;
                    if (sailMode) {
                        isGC = sailMode.textContent === '0';
                    } else if (dist < opt.assumeGCAbove) {
                        isGC = false;
                    }

                    route_geometry.push({
                            type: 'Feature',
                            geometry: {
                                coordinates: [prevCoords, coords],
                                type: 'LineString'
                            },
                            properties: {
                                leg_type: isGC ? 'GreatCircle' : 'Rhumbline'
                            }
                    });
                }

                route_geometry.push(wPoint);

                prevCoords = coords;
            });

            return {
                route: { 
                    routeId: '',
                    route_geometry },
                extras: {
                    // Plan related data.

                }
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static export(plan: VoyagePlan, options = {}): string
    {
        const opt = {
            ...options
        }

        // Bail out if no valid route is selected.
        if (!plan.currentSelectedRouteId)
        {
            throw new Error("No valid route selected in plan!");
        }

        // Only include the selected route.
        const routeIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId) ?? plan.selectedRouteIndex;
        const route = plan.routes[routeIndex];

        const xmlString = '<RouteModel xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns="http://www.sam-electronics.de/2010/reducedRouteModel.xsd"/>';
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml"); //important to use "text/xml"
        const routeModel = xmlDoc.documentElement;

        const routeName = document.createElementNS('http://www.sam-electronics.de/2010/reducedRouteModel.xsd', 'Name');
        routeModel.appendChild(routeName);
        routeName.setAttribute('xmlns','');

        const prunedRoute = pruneRouteGeometry(route.route_geometry);

        let points = <VoyageRoutePointFeature[]>prunedRoute.filter(item => item.geometry.type === "Point");

        const legs = <VoyageRouteLineFeature[]>prunedRoute.filter(item => item.geometry.type === "LineString");

        const addNSElement = (tagName: string, text?: string) => {
            const el = document.createElementNS('http://www.sam-electronics.de/2010/reducedRouteModel.xsd', tagName);
            if (text !== undefined) {
                el.appendChild(document.createTextNode(text));
            }
            return el;
        }

        points.forEach(point => {
            const wp = addNSElement('Waypoints');
            wp.setAttribute('xmlns','');
            
            wp.appendChild(addNSElement('Name', point.properties.name || `WP${points.indexOf(point) + 1}`));
            
            wp.appendChild(addNSElement('Latitude', `${toRadians(point.geometry.coordinates[1])}`));
            wp.appendChild(addNSElement('Longitude', `${toRadians(point.geometry.coordinates[0])}`));
            
            const leg = legForEndPoint(legs, point.geometry.coordinates, 1);
            let sailMode = '1';
            if (leg && leg.properties && leg.properties.leg_type === 'GreatCircle') sailMode = '0';
            wp.appendChild(addNSElement('SailMode', sailMode));

            wp.appendChild(addNSElement('Radius', '123'));
            
            routeModel.appendChild(wp);
        });

        const xmlSer = new XMLSerializer();
        const result = '<?xml version="1.0"?>\r\n' + xmlSer.serializeToString(xmlDoc);

        return result;
    }

    private static _parseXmlDocument(str: string): Document {
        const parser = new XMLDOMParser();
        const doc = parser.parseFromString(str, 'text/xml');
        const parseError = NacosConverter._querySelector(doc, 'parsererror');
        if (parseError) {
            throw new Error(parseError.textContent?.trim() || 'Invalid XML document.');
        }
        return doc;
    }

    private static _querySelector(node: Document | Element, tagName: string): Element | null {
        const elements = node.getElementsByTagName(tagName);
        return elements.length > 0 ? elements.item(0) : null;
    }
}
